// Builds the floor bid pad's grid of prices from a lot's bidding state.
//
// Pure — no reactivity, no state, no I/O — so it is not a composable, and
// laying out a grid of prices is the view layer's job the same way formatting
// one is. It sits beside the component that draws it and is deliberately NOT
// exported from `index.mjs`: it is `FloorBidPad`'s internals, not API.
//
// The pad has two phases and they are different grids:
//
//   BIDDING — the first cell is the standing bid as a reference mark, then the
//     asking price and five steps of the increment down the left column. The
//     right column continues at 5× the increment, which is what produces the
//     familiar 100 / 500 pad from a 100 increment: the clerk's thumb has a
//     fine ladder and a coarse one without re-aiming.
//
//   ASKING — nobody has bid, so the auctioneer is fishing DOWNWARD. The grid
//     runs up to the current ask (the last cell, marked) with thirteen steps
//     below it, and a step that would price the lot at or below zero renders
//     as a blank cell rather than a negative one.

/** Both phases lay out the same 7×2 block; the console's slot is sized to it. */
const ROW_COUNT = 7;
const COLUMN_COUNT = 2;

/** The coarse column steps this many increments at a time. */
const COARSE_MULTIPLE = 5;

/** Used when a lot arrives with no increment set, which the runtime allows. */
const FALLBACK_INCREMENT = 100;

/** Pairs a flat cell list into rows, column-major for the two-ladder layout. */
function toRows(firstColumn, secondColumn) {
    return Array.from({ length: ROW_COUNT }, (_, row) => [firstColumn[row], secondColumn[row]]);
}

/**
 * @param {{ currentBid: ?number, askingBid: ?number, increment: ?number }} bidding
 * @returns {{ rows: Array, asking: number, currentBid: ?number, increment: number }}
 */
export function buildBidPad({ currentBid = null, askingBid = 0, increment = null } = {}) {
    const step = increment || FALLBACK_INCREMENT;
    const asking = askingBid || 0;
    const hasBids = currentBid != null && currentBid > 0;

    if (hasBids) {
        const fine = [{ price: currentBid, tag: 'CURRENT' }];
        for (let index = 0; index < ROW_COUNT - 1; index += 1) {
            fine.push({ price: asking + index * step });
        }

        const coarseStep = step * COARSE_MULTIPLE;
        const coarseStart = asking + (ROW_COUNT - 1) * step + (coarseStep - step);
        const coarse = Array.from({ length: ROW_COUNT }, (_, index) => ({
            price: coarseStart + index * coarseStep,
        }));

        return { rows: toRows(fine, coarse), asking, currentBid, increment: step };
    }

    // Snap to the increment so the ladder lands on round numbers even when the
    // ask does not.
    const cellCount = ROW_COUNT * COLUMN_COUNT;
    const askFloor = Math.floor(asking / step) * step;
    const cells = Array.from({ length: cellCount }, (_, index) => {
        const price = askFloor - (cellCount - 1 - index) * step;
        if (index === cellCount - 1) return { price, tag: 'ASKING' };
        return { price: price > 0 ? price : null };
    });

    const rows = Array.from({ length: ROW_COUNT }, (_, row) => [cells[row * 2], cells[row * 2 + 1]]);
    return { rows, asking, currentBid: null, increment: step };
}
